// Static export means there is no server to read the request IP, so the
// visitor's country is resolved in the browser via a public geolocation API.
// Two independent endpoints are queried in order so a single provider being
// down or rate-limited does not break detection.
const GEO_ENDPOINTS = ["https://ipapi.co/json/", "https://ipwho.is/"] as const;

// Every outbound call gets a timeout; one slow provider must not stall the UI.
const REQUEST_TIMEOUT_MS = 2500;

let countryCodeRequest: Promise<string | null> | null = null;

function readCountryCode(payload: unknown): string | null {
  if (typeof payload !== "object" || payload === null) {
    return null;
  }

  const record = payload as Record<string, unknown>;
  const code = record.country_code ?? record.countryCode ?? record.country;

  return typeof code === "string" ? code.toUpperCase() : null;
}

async function fetchCountryCode(url: string): Promise<string | null> {
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

  try {
    const response = await fetch(url, {
      signal: controller.signal,
      headers: { Accept: "application/json" },
    });

    if (!response.ok) {
      return null;
    }

    return readCountryCode(await response.json());
  } catch {
    return null;
  } finally {
    window.clearTimeout(timeout);
  }
}

// Cached module-wide so pilot locale detection and the consent banner share a
// single network request instead of each firing their own lookup.
export async function detectVisitorCountryCode(): Promise<string | null> {
  countryCodeRequest ??= (async () => {
    for (const url of GEO_ENDPOINTS) {
      const countryCode = await fetchCountryCode(url);

      if (countryCode) {
        return countryCode;
      }
    }

    return null;
  })();

  return countryCodeRequest;
}
